import { useEffect, useState } from 'react';
import type { PersonalizationSettings, ResponseStyle, Transaction } from '@volt/shared';
import { formatKopecks, rublesToKopecks } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useTheme } from '../state/theme.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { LOCALE_LABELS, LOCALES } from '../i18n/index.js';
import { getTelegramUser } from '../telegram/webapp.js';
import { formatDateTime, extractErrorCode } from '../lib/dom.js';
import { initEruda } from '../lib/eruda.js';
import { Modal } from './Modal.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Icon } from './Icon.js';
import type { ThemeMode } from '../state/theme.js';


export interface SettingsScreenProps {
  open: boolean;
  onClose: () => void;
}

/** Full-screen settings sheet: balance/topup, theme, language, profile, and data. */
export function SettingsScreen({ open, onClose }: SettingsScreenProps) {
  const { t, locale, setLocale } = useI18n();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const { data, api, isAdmin } = useSession();
  const { toast: uiToast, openAdmin } = useUI();

  const [topupAmount, setTopupAmount] = useState(500);
  const [topupLoading, setTopupLoading] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txOffset, setTxOffset] = useState(0);
  const [txHasMore, setTxHasMore] = useState(true);
  const [txLoaded, setTxLoaded] = useState(false);
  const [txVisible, setTxVisible] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [localeOpen, setLocaleOpen] = useState(false);
  const [personalization, setPersonalization] = useState<PersonalizationSettings>({
    customInstructions: '',
    responseStyle: 'default',
    rules: [],
  });
  const [newRule, setNewRule] = useState('');
  const [personalizationLoading, setPersonalizationLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    void api.getPersonalization().then(({ personalization: p }) => setPersonalization(p)).catch(() => undefined);
  }, [api, open]);

  async function handleSavePersonalization(patch: Partial<PersonalizationSettings>) {
    setPersonalizationLoading(true);
    try {
      const updated = { ...personalization, ...patch };
      const result = await api.updatePersonalization(updated);
      setPersonalization(result.personalization);
      uiToast(t('settings.personalizationSaved'), 'success');
    } catch (error: unknown) {
      const code = extractErrorCode(error);
      uiToast(t(('error.' + code) as Parameters<typeof t>[0]), 'error');
    } finally {
      setPersonalizationLoading(false);
    }
  }

  function handleAddRule() {
    const trimmed = newRule.trim();
    if (!trimmed) return;
    const rules = [...personalization.rules, trimmed];
    setPersonalization((prev) => ({ ...prev, rules }));
    setNewRule('');
    void handleSavePersonalization({ rules });
  }

  function handleRemoveRule(index: number) {
    const rules = personalization.rules.filter((_, i) => i !== index);
    setPersonalization((prev) => ({ ...prev, rules }));
    void handleSavePersonalization({ rules });
  }

  const user = getTelegramUser();
  const balance = data?.balanceKopecks ?? 0;

  async function handleTopup() {
    setTopupLoading(true);
    try {
      const kopecks = rublesToKopecks(topupAmount);
      const result = await api.topup(kopecks);
      window.open(result.paymentUrl, '_blank');
    } catch (error: unknown) {
      const code = extractErrorCode(error);
      uiToast(t(('error.' + code) as Parameters<typeof t>[0]), 'error');
    } finally {
      setTopupLoading(false);
    }
  }

  async function loadTransactions() {
    if (txLoading) return;
    setTxLoading(true);
    try {
      const result = await api.getTransactions(20, txOffset);
      setTransactions((prev) => [...prev, ...result.transactions]);
      setTxOffset((prev) => prev + result.transactions.length);
      setTxHasMore(result.transactions.length === 20);
      setTxLoaded(true);
    } catch {
      // ignore
    } finally {
      setTxLoading(false);
    }
  }

  async function handleExport() {
    await api.exportData().catch(() => undefined);
    uiToast(t('settings.exportData'), 'success');
  }

  async function handleDeleteAccount() {
    try {
      await api.deleteAccount();
      window.location.reload();
    } catch (error: unknown) {
      const code = extractErrorCode(error);
      uiToast(t(('error.' + code) as Parameters<typeof t>[0]), 'error');
    }
  }

  const THEME_OPTIONS: { value: ThemeMode; label: Parameters<typeof t>[0] }[] = [
    { value: 'light', label: 'settings.themeLight' },
    { value: 'dark', label: 'settings.themeDark' },
    { value: 'system', label: 'settings.themeSystem' },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('settings.title')}
      closeLabel={t('common.close')}
      className="settings-modal"
    >
      <div className="settings-content">
        {/* Profile */}
        <section className="settings-section">
          <h3 className="settings-section-title">{t('settings.profile')}</h3>
          <div className="settings-profile">
            {user?.photo_url ? (
              <img src={user.photo_url} alt="" className="profile-avatar" />
            ) : (
              <div className="profile-avatar profile-avatar-placeholder">
                <Icon name="user" size={24} />
              </div>
            )}
            <div className="profile-info">
              {(user?.first_name || user?.last_name) && (
                <p className="profile-name">
                  {[user.first_name, user.last_name].filter(Boolean).join(' ')}
                </p>
              )}
              {user?.username && (
                <p className="profile-username">@{user.username}</p>
              )}
            </div>
          </div>
        </section>

        {/* Balance & Top-up */}
        <section className="settings-section">
          <h3 className="settings-section-title">{t('settings.balance')}</h3>
          <p className="settings-balance">{formatKopecks(balance)}</p>

          <div className="topup-custom">
            <label className="settings-label" htmlFor="topup-custom-input">
              {t('settings.topupAmount')}
            </label>
            <input
              id="topup-custom-input"
              type="number"
              className="field"
              min={10}
              max={100000}
              value={topupAmount}
              onChange={(event) => setTopupAmount(Number(event.target.value))}
            />
          </div>

          <button
            type="button"
            className="btn btn-primary topup-submit"
            disabled={topupLoading || topupAmount < 10}
            onClick={() => void handleTopup()}
          >
            {topupLoading ? <span className="spinner" style={{ width: 18, height: 18 }} /> : <Icon name="card" size={18} />}
            {t('settings.topupSubmit')}
          </button>
        </section>

        {/* Transaction history */}
        <section className="settings-section">
          <div className="settings-row">
            <h3 className="settings-section-title">{t('settings.transactions')}</h3>
            {txLoaded && transactions.length > 0 && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setTxVisible((v) => !v)}
              >
                <Icon name={txVisible ? 'chevronUp' : 'chevronDown'} size={16} />
              </button>
            )}
          </div>
          {!txLoaded ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void loadTransactions()}
            >
              {t('settings.showTransactions')}
            </button>
          ) : transactions.length === 0 ? (
            <p className="settings-empty">{t('settings.noTransactions')}</p>
          ) : txVisible ? (
            <div className="tx-list">
              {transactions.map((tx) => (
                <div key={tx.id} className={`tx-item tx-${tx.type}`}>
                  <span className="tx-reason">{tx.reason}</span>
                  <span className={`tx-amount ${tx.amountKopecks >= 0 ? 'tx-plus' : 'tx-minus'}`}>
                    {tx.amountKopecks >= 0 ? '+' : ''}
                    {formatKopecks(tx.amountKopecks)}
                  </span>
                  <span className="tx-date">{formatDateTime(tx.createdAt)}</span>
                </div>
              ))}
              {txHasMore && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={txLoading}
                  onClick={() => void loadTransactions()}
                >
                  {txLoading ? (
                    <span className="spinner" style={{ width: 16, height: 16 }} />
                  ) : null}
                  {t('settings.loadMore')}
                </button>
              )}
            </div>
          ) : null}
        </section>

        {/* Appearance */}
        <section className="settings-section">
          <h3 className="settings-section-title">{t('settings.appearance')}</h3>
          <div className="settings-row">
            <span className="settings-label">{t('settings.theme')}</span>
            <div className="segmented">
              {THEME_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`segmented-btn${themeMode === value ? ' is-active' : ''}`}
                  onClick={() => setThemeMode(value)}
                >
                  {t(label)}
                </button>
              ))}
            </div>
          </div>
          <div className="settings-row">
            <span className="settings-label" id="locale-label">
              {t('settings.language')}
            </span>
            <div className={`custom-select${localeOpen ? ' is-open' : ''}`} role="combobox" aria-haspopup="listbox" aria-expanded={localeOpen} aria-labelledby="locale-label">
              <button
                type="button"
                className="custom-select-trigger"
                onClick={() => setLocaleOpen((v) => !v)}
                onBlur={() => setTimeout(() => setLocaleOpen(false), 150)}
              >
                <span>{LOCALE_LABELS[locale]}</span>
                <Icon name="chevronDown" size={14} />
              </button>
              {localeOpen && (
                <ul className="custom-select-menu" role="listbox" aria-labelledby="locale-label">
                  {LOCALES.map((l) => (
                    <li
                      key={l}
                      role="option"
                      aria-selected={locale === l}
                      className={`custom-select-option${locale === l ? ' is-selected' : ''}`}
                      onMouseDown={() => {
                        setLocale(l);
                        setLocaleOpen(false);
                      }}
                    >
                      {LOCALE_LABELS[l]}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* Admin (visible only to configured admins) */}
        {isAdmin && (
          <section className="settings-section">
            <h3 className="settings-section-title">{t('admin.title')}</h3>
            <button
              type="button"
              className="btn btn-ghost settings-action-btn"
              onClick={() => {
                onClose();
                openAdmin();
              }}
            >
              <Icon name="settings" size={18} />
              {t('admin.title')}
            </button>
            <button
              type="button"
              className="btn btn-ghost settings-action-btn"
              onClick={() => void initEruda()}
            >
              <Icon name="settings" size={18} />
              {t('admin.debugConsole')}
            </button>
          </section>
        )}

        {/* Personalization */}
        <section className="settings-section">
          <h3 className="settings-section-title">{t('settings.personalization')}</h3>

          {/* Custom instructions */}
          <div className="settings-field full-width">
            <label className="settings-label">{t('settings.customInstructions')}</label>
            <span className="settings-hint">{t('settings.customInstructionsHint')}</span>
            <textarea
              className="composer-input settings-textarea"
              rows={3}
              value={personalization.customInstructions}
              onChange={(e) => setPersonalization((prev) => ({ ...prev, customInstructions: e.target.value }))}
              onBlur={() => void handleSavePersonalization({ customInstructions: personalization.customInstructions })}
              disabled={personalizationLoading}
            />
          </div>

          {/* Response style */}
          <div className="settings-field full-width">
            <span className="settings-label">{t('settings.responseStyle')}</span>
            <div className="segmented segmented-scroll">
              {(['default', 'formal', 'teacher', 'concise'] as ResponseStyle[]).map((style) => (
                <button
                  key={style}
                  type="button"
                  className={`segmented-btn${personalization.responseStyle === style ? ' is-active' : ''}`}
                  onClick={() => {
                    setPersonalization((prev) => ({ ...prev, responseStyle: style }));
                    void handleSavePersonalization({ responseStyle: style });
                  }}
                >
                  {t((`settings.responseStyle${style.charAt(0).toUpperCase()}${style.slice(1)}`) as Parameters<typeof t>[0])}
                </button>
              ))}
            </div>
          </div>

          {/* Rules */}
          <div className="settings-field full-width">
            <label className="settings-label">{t('settings.rules')}</label>
            <span className="settings-hint">{t('settings.rulesHint')}</span>
            {personalization.rules.map((rule, i) => (
              <div key={i} className="rule-item">
                <span className="rule-text">{rule}</span>
                <button
                  type="button"
                  className="icon-btn rule-remove"
                  onClick={() => handleRemoveRule(i)}
                  aria-label="Remove rule"
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
            ))}
            <div className="rule-add-row">
              <input
                type="text"
                className="composer-input rule-input"
                placeholder={t('settings.ruleInputPlaceholder')}
                value={newRule}
                onChange={(e) => setNewRule(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRule(); } }}
              />
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleAddRule}
                disabled={!newRule.trim()}
              >
                {t('settings.addRule')}
              </button>
            </div>
          </div>
        </section>

        {/* Data */}
        <section className="settings-section">
          <h3 className="settings-section-title">{t('settings.data')}</h3>
          <button
            type="button"
            className="btn btn-ghost settings-action-btn"
            onClick={() => void handleExport()}
          >
            <Icon name="download" size={18} />
            {t('settings.exportData')}
          </button>
          <button
            type="button"
            className="btn btn-danger settings-action-btn"
            onClick={() => setConfirmDelete(true)}
          >
            <Icon name="trash" size={18} />
            {t('settings.deleteAccount')}
          </button>
        </section>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={t('settings.deleteAccountTitle')}
        message={t('settings.deleteAccountConfirm')}
        onConfirm={() => void handleDeleteAccount()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Modal>
  );
}
